import { isEmpty } from '@intelligence/v3/utils/common';
import { formatKMBWithPlaceholder } from '@intelligence/v3/utils/number';
import { renderBarChartTooltip } from '../../Tooltip';
import {
  HORIZONTAL_BAR_CHART_COLORS,
  HORIZONTAL_BAR_WIDTH,
  HORIZONTAL_BAR_MAX_WIDTH,
  HORIZONTAL_BAR_MIN_WIDTH,
  HIGHLIGHT_BG_COLOR,
  HORIZONTAL_BAR_GRID_WITH_LABEL,
  HORIZONTAL_BAR_GRID_WITHOUT_LABEL,
} from '../constants';

/**
 * 对数据按数值排序（从大到小）
 * @param data 图表数据
 * @param sortByValue 是否排序
 * @returns 排序后的数据
 */
const sortDataByValue = (data: IHorizontalBarChartData[], sortByValue = true): IHorizontalBarChartData[] => {
  if (!sortByValue) {
    return data;
  }
  // 从小到大排序，因为 Y 轴是从下往上显示的，所以最大值在最上面
  return [...data].sort((a, b) => {
    const aValue = a?.value;
    const bValue = b?.value;

    if (isEmpty(aValue)) {
      return -1;
    }

    if (isEmpty(bValue)) {
      return 1;
    }

    return (aValue as number) - (bValue as number);
  });
};

/**
 * 生成 series 数据
 * @param data 图表数据
 * @param showLabel 是否显示柱子右侧标签
 * @param valueLabelFormatter 数值标签格式化函数
 * @returns series 数据数组
 */
const getHorizontalBarChartSeriesData = (
  data: IHorizontalBarChartData[],
  showLabel?: boolean,
  valueLabelFormatter?: TValueFormatter | null,
): any[] => {
  const formatter = valueLabelFormatter || formatKMBWithPlaceholder;

  return data.map((item, index) => ({
    type: 'bar',
    name: item.name,
    value: item.value,
    payload: item.payload || {},
    itemStyle: {
      color: HORIZONTAL_BAR_CHART_COLORS[index % HORIZONTAL_BAR_CHART_COLORS.length],
    },
    label: {
      show: showLabel !== false,
      position: 'right',
      color: '#444',
      distance: 4,
      fontSize: 12,
      formatter: (params: any) => formatter(params.value),
    },
  }));
};

/**
 * 生成高亮 markArea 配置
 * @param data 图表数据
 * @param highlightName 高亮的数据项名称
 * @returns markArea 配置
 */
const getHorizontalBarChartMarkArea = (data: IHorizontalBarChartData[], highlightName?: string): any => {
  if (!highlightName) {
    return undefined;
  }

  const highlightIndex = data.findIndex((item) => item.name === highlightName);
  if (highlightIndex === -1) {
    return undefined;
  }

  return {
    itemStyle: {
      color: HIGHLIGHT_BG_COLOR,
    },
    data: [[{ yAxis: highlightIndex }, { yAxis: highlightIndex }]],
  };
};

/**
 * 生成 X 轴配置（数值轴）
 * @param xAxisLabelFormatter X轴标签格式化函数
 * @returns X轴配置
 */
const getHorizontalBarChartXAxisConfig = (xAxisLabelFormatter?: TValueFormatter | null): any => {
  const formatter = xAxisLabelFormatter || formatKMBWithPlaceholder;

  return {
    type: 'value',
    axisLabel: {
      color: '#838697',
      fontSize: 12,
      formatter: (value: number) => formatter(value),
    },
    splitLine: {
      lineStyle: {
        type: 'dashed',
        color: '#E5E5E5',
      },
    },
    axisLine: {
      show: false,
    },
    axisTick: {
      show: false,
    },
  };
};

/**
 * 生成 Tooltip 配置
 * @param data 图表数据
 * @param metricName 指标名称
 * @param valueFormatter 数值格式化函数
 * @param nameFormatter 名称格式化函数
 * @param customTooltipFormatter 自定义 Tooltip 格式化函数
 * @returns Tooltip 配置
 */
const getHorizontalBarChartTooltipConfig = (
  data: IHorizontalBarChartData[],
  metricName?: string,
  valueFormatter?: TValueFormatter | null,
  nameFormatter?: TNameFormatter | null,
  customTooltipFormatter?: ((params: any) => string) | null,
): any => {
  const valFormatter = valueFormatter || formatKMBWithPlaceholder;

  // 如果提供了自定义 Tooltip 格式化函数，直接使用
  if (customTooltipFormatter) {
    return {
      trigger: 'axis',
      padding: 0,
      borderWidth: 0,
      backgroundColor: 'rgba(255, 255, 255, 0)',
      confine: true,
      hideDelay: 200,
      triggerOn: 'mousemove',
      axisPointer: {
        type: 'shadow',
      },
      formatter: customTooltipFormatter,
    };
  }

  return {
    trigger: 'axis',
    padding: 0,
    borderWidth: 0,
    backgroundColor: 'rgba(255, 255, 255, 0)',
    confine: true,
    hideDelay: 200,
    triggerOn: 'mousemove',
    axisPointer: {
      type: 'shadow',
    },
    formatter: (params: any[]) => {
      if (!params || params.length === 0) {
        return '';
      }

      const firstParam = params[0];

      const dataList = params.map((param) => {
        const displayName = nameFormatter
          ? nameFormatter(metricName || param.seriesName || '')
          : metricName || param.seriesName || '';

        return {
          color: param.color || HORIZONTAL_BAR_CHART_COLORS[param.dataIndex % HORIZONTAL_BAR_CHART_COLORS.length],
          name: displayName,
          value: valFormatter(param.value),
          payload: param.data?.payload || {},
        };
      });

      return renderBarChartTooltip({
        title: firstParam.name,
        metricName,
        dataList,
      });
    },
  };
};

/**
 * 生成 Y 轴标签格式化函数
 * @param data 图表数据
 * @param highlightName 高亮的数据项名称
 * @param yAxisLabelFormatter y轴刻度文案formatter
 * @returns Y轴标签格式化函数
 */
const getHorizontalBarChartYAxisLabelFormatter = (
  data: IHorizontalBarChartData[],
  highlightName?: string,
  yAxisLabelFormatter?: ((value: string) => string) | null,
): ((value: string) => string) => {
  if (typeof yAxisLabelFormatter === 'function') {
    return yAxisLabelFormatter;
  }

  return (value: string): string => {
    const isHighlight = highlightName && value === highlightName;
    const nameStyle = isHighlight ? 'highlightName' : 'normalName';
    return `{${nameStyle}|${value}}`;
  };
};

/**
 * 获取 Y 轴基础 rich 配置（名称样式）
 * @param highlightName 高亮的数据项名称
 * @returns Y轴 rich 配置
 */
const getHorizontalBarChartYAxisRich = (highlightName?: string): Record<string, any> => {
  const baseRich: Record<string, any> = {
    normalName: {
      color: '#000',
      opacity: 0.45,
      fontSize: 12,
      align: 'right',
    },
  };

  if (highlightName) {
    baseRich.highlightName = {
      color: '#262832',
      fontSize: 12,
      fontWeight: 'bold',
      align: 'right',
    };
  }

  return baseRich;
};

/**
 * 获取横向柱状图动态配置
 * @param params 配置参数
 * @returns ECharts option 对象
 */
export const getHorizontalBarChartDynamicOption = (params: IGetHorizontalBarChartDynamicOptionParams): any => {
  const {
    data,
    metricName,
    highlightName,
    showLabel = true,
    sortByValue = true,
    xAxisLabelFormatter,
    yAxisLabelFormatter,
    valueLabelFormatter,
    nameFormatter,
    tooltipFormatter,
  } = params;

  if (!data || data.length === 0) {
    return {
      xAxis: getHorizontalBarChartXAxisConfig(xAxisLabelFormatter),
      yAxis: { data: [] },
      series: [],
    };
  }

  // 排序数据
  const sortedData = sortDataByValue(data, sortByValue);

  // 生成 series 数据
  const seriesData = getHorizontalBarChartSeriesData(sortedData, showLabel, valueLabelFormatter);

  // 生成 markArea（高亮）
  const markArea = getHorizontalBarChartMarkArea(sortedData, highlightName);

  // 生成 Y 轴 rich 配置
  const yAxisRich = getHorizontalBarChartYAxisRich(highlightName);

  return {
    grid: showLabel ? HORIZONTAL_BAR_GRID_WITH_LABEL : HORIZONTAL_BAR_GRID_WITHOUT_LABEL,
    tooltip: getHorizontalBarChartTooltipConfig(
      sortedData,
      metricName,
      valueLabelFormatter,
      nameFormatter,
      tooltipFormatter,
    ),
    xAxis: getHorizontalBarChartXAxisConfig(xAxisLabelFormatter),
    yAxis: {
      type: 'category',
      axisTick: {
        show: false,
      },
      axisLine: {
        show: false,
      },
      axisLabel: {
        color: '#000',
        opacity: 0.45,
        fontSize: 12,
        margin: 8,
        formatter: getHorizontalBarChartYAxisLabelFormatter(sortedData, highlightName, yAxisLabelFormatter),
        rich: yAxisRich,
      },
      data: sortedData.map((item) => item.name),
    },
    series: [
      {
        type: 'bar',
        barWidth: HORIZONTAL_BAR_WIDTH,
        barMaxWidth: HORIZONTAL_BAR_MAX_WIDTH,
        barMinWidth: HORIZONTAL_BAR_MIN_WIDTH,
        data: seriesData,
        markArea,
      },
    ],
  };
};
